/**
 * TopBar — Fixed top navigation bar (48px).
 * LEFT: AWENI logo + current module name
 * RIGHT: notification bell (badge) + profile avatar (initials)
 * Collapses on scroll down, reappears on scroll up.
 */

import type { AweniContext } from '../context/AweniContext';
import { NativeService } from '../services/NativeService';

interface TopBarCallbacks {
  onNotificationsClick: () => void;
  onProfileClick: () => void;
}

let _bar: HTMLElement | null = null;
let _moduleLabel: HTMLSpanElement | null = null;
let _bellBadge: HTMLSpanElement | null = null;
let _avatarBtn: HTMLButtonElement | null = null;
let _ctx: AweniContext | null = null;
let _lastScrollY = 0;
let _hidden = false;

export function mountTopBar(
  container: HTMLElement,
  ctx: AweniContext,
  callbacks: TopBarCallbacks,
): void {
  _ctx = ctx;

  // Sync native status bar style with current theme
  NativeService.setStatusBarStyle(ctx.theme.isDark ? 'dark' : 'light');

  const bar = container;
  bar.setAttribute('role', 'banner');
  bar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 48px;
    z-index: 100;
    background: var(--topbar-bg);
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    transition: transform 250ms ease;
    box-shadow: var(--shadow-sm);
  `;
  _bar = bar;

  // --- Left: logo + module name ---
  const left = document.createElement('div');
  left.style.cssText = 'display:flex;align-items:center;gap:8px;min-width:0;';

  const logo = document.createElement('span');
  logo.textContent = 'AWENI';
  logo.style.cssText = `
    font-weight: 800;
    font-size: 18px;
    color: var(--accent);
    letter-spacing: -0.5px;
    flex-shrink: 0;
  `;

  const sep = document.createElement('span');
  sep.textContent = '\u00b7';
  sep.style.cssText = 'color:var(--text-secondary);font-size:16px;flex-shrink:0;';

  const label = document.createElement('span');
  label.style.cssText = `
    font-size: 14px;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
  label.textContent = ctx.i18n.t('nav_home');
  _moduleLabel = label;

  left.append(logo, sep, label);

  // --- Right: bell + avatar ---
  const right = document.createElement('div');
  right.style.cssText = 'display:flex;align-items:center;gap:8px;flex-shrink:0;';

  // Notification bell button — WCAG 2.1 AA: stroke + size + hit-target
  // explicitly tuned to pass 1.4.11 non-text 3:1 contrast in BOTH themes.
  // (Was 2px stroke on 20×20 with currentColor inherited from a
  // var(--text-primary) inline style — observed as low-contrast in
  // Firefox light mode.) We now use a class with theme-specific color
  // set in CSS so it's explicit and overridable.
  const bellBtn = document.createElement('button');
  bellBtn.className = 'aweni-topbar-icon-btn';
  bellBtn.setAttribute('aria-label', ctx.i18n.t('topbar_notifications'));
  bellBtn.style.cssText = `
    position: relative;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 120ms ease;
  `;
  bellBtn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>`;

  const badge = document.createElement('span');
  badge.style.cssText = `
    position: absolute;
    top: 4px;
    right: 4px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: var(--notification-dot);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: none;
    align-items: center;
    justify-content: center;
    line-height: 1;
  `;
  _bellBadge = badge;
  bellBtn.appendChild(badge);
  bellBtn.addEventListener('click', callbacks.onNotificationsClick);

  // Profile avatar button
  const avatarBtn = document.createElement('button');
  avatarBtn.setAttribute('aria-label', ctx.i18n.t('topbar_profile'));
  avatarBtn.style.cssText = `
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--accent);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
  `;
  _updateAvatarText(avatarBtn, ctx);
  _avatarBtn = avatarBtn;
  avatarBtn.addEventListener('click', callbacks.onProfileClick);

  right.append(bellBtn, avatarBtn);
  bar.append(left, right);

  // Initial badge update
  _refreshBadge(ctx);

  // Scroll collapse — watch the main viewport
  const viewport = document.getElementById('aweni-main');
  if (viewport) {
    viewport.addEventListener('scroll', () => {
      const y = viewport.scrollTop;
      if (y > _lastScrollY && y > 48 && !_hidden) {
        _hidden = true;
        if (_bar) _bar.style.transform = 'translateY(-100%)';
      } else if (y < _lastScrollY && _hidden) {
        _hidden = false;
        if (_bar) _bar.style.transform = 'translateY(0)';
      }
      _lastScrollY = y;
    }, { passive: true });
  }
}

/**
 * Update the module name shown in the top bar.
 */
export function setTopBarModuleName(moduleId: string): void {
  if (_moduleLabel && _ctx) {
    // Use i18n key like nav_home, nav_games, etc.
    const key = `nav_${moduleId}`;
    const translated = _ctx.i18n.t(key);
    _moduleLabel.textContent = translated !== key ? translated : moduleId;
  }
}

/**
 * Refresh the unread notification badge count.
 */
export function refreshTopBarBadge(): void {
  if (_ctx) _refreshBadge(_ctx);
}

/**
 * Refresh the profile avatar initials.
 */
export function refreshTopBarAvatar(): void {
  if (_avatarBtn && _ctx) _updateAvatarText(_avatarBtn, _ctx);
}

function _refreshBadge(ctx: AweniContext): void {
  if (!_bellBadge) return;
  const count = ctx.notifications.unreadCount;
  if (count > 0) {
    _bellBadge.textContent = count > 99 ? '99+' : String(count);
    _bellBadge.style.display = 'flex';
  } else {
    _bellBadge.style.display = 'none';
  }
}

function _updateAvatarText(btn: HTMLButtonElement, ctx: AweniContext): void {
  const profile = ctx.auth.activeProfile;
  const user = ctx.auth.user;
  const name = profile?.name || user?.name || '?';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2);
  btn.textContent = initials.toUpperCase() || '?';
}
